import React, { useMemo, useCallback, useEffect, useState } from 'react';
import Tabs from '../../../components/Tabs/Tabs';
import { Copier } from '../../../../components/copier';
import CopyIcon from '../../../assets/svg/icons/common/copy';
import styles from './style.module.scss';
import useCommonStore from '../../../stores/common';
import useUserStore from '../../../stores/user';
import useManagerApprovalStore from '../../../stores/managerApproval';
import ButtonIcon from '../../../components/ButtonIcon/ButtonIcon';
import ExpandDetailsIcon from '../../../assets/svg/icons/common/ExpandDetailsIcon';
import CollapseDetailsIcon from '../../../assets/svg/icons/common/CollapseDetailsIcon';
import CloseIcon24 from '../../../assets/svg/icons/common/CloseIcon24';
import PendingTab from './components/PendingTab';
import ApprovedTab from './components/ApprovedTab';
import DeclinedTab from './components/DeclinedTab';
import toast from 'react-hot-toast';
import {
  approveApproval,
  declineApproval,
  fetchPowerappApprovals,
  fetchApprovalHistory,
  fetchPowerappsWithApprovalCounts
} from '../../../services/managerApproval';
import { getMerchantInfo } from '../../../../api';

// Unified function to fetch approvals (loader removed)
const useFetchApprovals = ({
  selectedPowerappId,
  // setLoading removed
  toast,
}) => {
  const fetchApprovals = useCallback(
    async (status) => {
      if (!selectedPowerappId) return;
      try {
        const response = await fetchPowerappApprovals({
          powerappId: selectedPowerappId,
          params: {
            status,
          },
        });
        if (!response?.status && toast) {
          toast.error('Failed to load approvals', { duration: 5000 });
        }
        return response;
      } catch (error) {
        if (toast) {
          toast.error('Error loading approvals', { duration: 5000 });
        }
        console.error('Error loading approval data:', error);
      }
    },
    [selectedPowerappId, toast]
  );
  return fetchApprovals;
};

const Details = () => {
  const expandDetails = useCommonStore((state) => state.expandDetails);
  const toggleExpandDetails = useCommonStore((state) => state.toggleExpandDetails);
  const toggleShowDetails = useCommonStore((state) => state.toggleShowDetails);
  const userOrgId = useUserStore((state) => state.organizationId);
  const {
    powerappApprovals,
    powerappsWithApprovals,
    selectedPowerappId,
    selectedPowerapp,
    currentPage,
    pageCount,
    itemsPerPage,
    setCurrentPage,
    setSelectedPowerappId,
    setSelectedPowerapp,
    setPowerappApprovals,
  } = useManagerApprovalStore();

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [merchantInfo, setMerchantInfo] = useState(null);

  // Get status based on active tab
  const getStatusForTab = useCallback((tabIndex) => {
    switch (tabIndex) {
      case 0:
        return 'pending';
      case 1:
        return 'approved';
      case 2:
        return 'declined';
      default:
        return 'pending';
    }
  }, []);

  // Get current status
  const currentStatus = useMemo(() => getStatusForTab(activeTabIndex), [activeTabIndex, getStatusForTab]);

  // Unified fetch function (setLoading removed)
  const fetchApprovals = useFetchApprovals({
    selectedPowerappId,
    toast,
  });

  // Reset to pending tab and load initial data when powerapp changes
  useEffect(() => {
    if (!selectedPowerappId) return;

    // Reset to pending tab
    setActiveTabIndex(0);

    const loadInitialData = async () => {
      try {
        const merchantInfo = await getMerchantInfo({ id: selectedPowerappId });
        setMerchantInfo(merchantInfo);
        await fetchApprovals(getStatusForTab(0)); // Always load pending items initially
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };

    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPowerappId]);

  const handleAction = useCallback(async (itemId, action, notes = '') => {
    try {
      let response;
      if (action === 'approve') {
        response = await approveApproval(itemId, notes);
      } else if (action === 'decline') {
        response = await declineApproval(itemId, notes);
      }

      if (response?.status) {
        toast.success(`Request ${action === 'approve' ? 'approved' : 'declined'} successfully`, { duration: 5000 });

        // Refresh both list and details data
        await Promise.all([
          // Refresh list data
          fetchPowerappsWithApprovalCounts(userOrgId, {
            page: currentPage,
            per_page: itemsPerPage
          }),
          // Refresh details data
          fetchApprovals(currentStatus)
        ]);
      } else {
        toast.error(action === 'approve' ? 'Failed to approve the request' : 'Failed to decline the request', { duration: 5000 });
      }
    } catch (error) {
      console.error(`Error ${action}ing request:`, error);
      const serverMessage = error?.response?.data?.message;
      toast.error(
        serverMessage ||
          (action === 'approve'
            ? 'Error approving the request'
            : 'Error declining the request'),
        { duration: 5000 }
      );
    } finally {
    }
  }, [selectedPowerappId, currentStatus, currentPage, itemsPerPage, userOrgId, fetchApprovals, toast]);

  const onCloseClick = useCallback(() => {
    setSelectedPowerappId(null);
    setSelectedPowerapp(null);
    setPowerappApprovals([]);
    setMerchantInfo(null);
    setActiveTabIndex(0);
    toggleShowDetails();
    if (expandDetails) {
      toggleExpandDetails();
    }
  }, [
    expandDetails,
    toggleExpandDetails,
    toggleShowDetails,
    setSelectedPowerappId,
    setSelectedPowerapp,
    setPowerappApprovals,
  ]);

  const items = useMemo(() => {
    return powerappApprovals || [];
  }, [powerappApprovals]);

  const selectedPowerappCounts = useMemo(() => {
    const powerapp = powerappsWithApprovals.find(p => p.powerapp_id === selectedPowerappId);
    return powerapp ? {
      pending: powerapp.pending_count || 0,
      approved: powerapp.approved_count || 0,
      declined: powerapp.declined_count || 0
    } : {
      pending: 0,
      approved: 0,
      declined: 0
    };
  }, [powerappsWithApprovals, selectedPowerappId]);

  const handleTabChange = useCallback(async (index) => {
    setActiveTabIndex(index);

    if (!selectedPowerappId) return;

    try {
      const status = getStatusForTab(index);

      await fetchApprovals(status);
    } catch (error) {
      // Error handling is already in fetchApprovals
    }
  }, [selectedPowerappId, getStatusForTab, fetchApprovals]);

  const tabs = useMemo(
    () => [
      {
        label: 'Pending',
        content: <PendingTab items={items} onAction={handleAction} isExpanded={expandDetails} appData={merchantInfo} />,
        badge: String(selectedPowerappCounts.pending),
        badgeStyle: 'info',
      },
      {
        label: 'Approved',
        content: <ApprovedTab items={items} isExpanded={expandDetails} appData={merchantInfo} />,
        badge: String(selectedPowerappCounts.approved),
        badgeStyle: 'success',
      },
      {
        label: 'Declined',
        content: <DeclinedTab items={items} isExpanded={expandDetails} appData={merchantInfo} />,
        badge: String(selectedPowerappCounts.declined),
        badgeStyle: 'danger',
      },
    ],
    [items, handleAction, selectedPowerappCounts, expandDetails],
  );

  return (
    <div className={`${styles.details} ${expandDetails ? styles.expanded : ''}`}>
      <div className={styles.details_head}>
        <button
          className={styles.expand_button}
          onClick={toggleExpandDetails}
          type="button"
        >
          {!expandDetails ? <ExpandDetailsIcon /> : <CollapseDetailsIcon />}
          <span>{!expandDetails ? 'Expand Details' : 'Minimize'}</span>
        </button>

        <ButtonIcon onClick={onCloseClick}>
          <CloseIcon24 />
        </ButtonIcon>
      </div>

      <div className={styles.merchantInfo}>
        <div className={styles.merchantInfoTop}>
          <div className={styles.merchantNameArea}>
            <div className={styles.merchantNameContainer}>
              <p className={styles.merchantName}>{selectedPowerapp?.dba || '-'}</p>
            </div>
          </div>
        </div>
        <div className={styles.infoRow}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>App ID:</span>
            <div className={styles.infoValue}>
              <span>{selectedPowerapp?.id || '-'}</span>
              <Copier text={selectedPowerapp?.id || ''}>
                <CopyIcon />
              </Copier>
            </div>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Organization:</span>
            <div className={styles.infoValue}>
              <span>{selectedPowerapp?.organization_name || '-'}</span>
              <Copier text={selectedPowerapp?.organization_name || ''}>
                <CopyIcon />
              </Copier>
            </div>
          </div>
        </div>
      </div>

      <div>
        <Tabs
          tabs={tabs}
          active_index={activeTabIndex}
          onSelect={handleTabChange}
        />
      </div>
    </div>
  );
};

export default Details;